import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import HubPage from './pages/HubPage';
import HubMainboard from './pages/HubMainboard';
import HubDashboard from './pages/HubDashboard';
import PublicBoard from './pages/PublicBoard';
import DashboardPage from './pages/DashboardPage';
import AdminControlPage from './pages/AdminControlPage';
import UserApprovalPage from './pages/UserApprovalPage';
import JobTitlesPage from './pages/JobTitlesPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import PointsPage from './pages/PointsPage';
import PointsConfigPage from './pages/PointsConfigPage';
import AppStatsPage from './pages/AppStatsPage';
import MessagesPage from './pages/MessagesPage';
import ChatPermissionsPage from './pages/ChatPermissionsPage';
import AppsPage from './pages/AppsPage';
import EventsPage from './pages/EventsPage';
import SettingsPage from './pages/SettingsPage';
import FleetPage from './pages/FleetPage';
import AppSettingsPage from './pages/AppSettingsPage';
import TrainingPage from './pages/TrainingPage';
import WatchTrainingPage from './pages/WatchTrainingPage';
import TxtConvPage from './pages/TxtConvPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isAdmin = ['master_admin', 'admin'].includes(user.role_id);
  if (!isAdmin) {
    return <Navigate to="/hub" replace />;
  }

  return children;
};

// Master Admin Route Component
const MasterAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role_id !== 'master_admin') {
    return <Navigate to="/hub" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public Board - Shareable link (no auth required) */}
      <Route path="/board/:boardId" element={<PublicBoard />} />
      
      {/* Watch Training - Public page for drivers (no auth required) */}
      <Route path="/watch/:linkToken" element={<WatchTrainingPage />} />
      
      {/* Hub - Main landing after login */}
      <Route path="/hub" element={
        <ProtectedRoute>
          <HubDashboard />
        </ProtectedRoute>
      } />
      
      {/* Hub Mainboard - Mobile-first design */}
      <Route path="/mainboard" element={
        <ProtectedRoute>
          <HubMainboard />
        </ProtectedRoute>
      } />
      
      {/* Old Hub Page - Legacy */}
      <Route path="/hub-legacy" element={
        <ProtectedRoute>
          <HubPage />
        </ProtectedRoute>
      } />
      
      {/* Admin pages */}
      <Route path="/dashboard" element={
        <AdminRoute>
          <DashboardPage />
        </AdminRoute>
      } />
      
      <Route path="/admin-control" element={
        <MasterAdminRoute>
          <AdminControlPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/approvals" element={
        <MasterAdminRoute>
          <UserApprovalPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/job-titles" element={
        <MasterAdminRoute>
          <JobTitlesPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/users" element={
        <AdminRoute>
          <UsersPage />
        </AdminRoute>
      } />
      
      <Route path="/roles" element={
        <MasterAdminRoute>
          <RolesPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/points" element={
        <AdminRoute>
          <PointsPage />
        </AdminRoute>
      } />
      
      <Route path="/points-config" element={
        <MasterAdminRoute>
          <PointsConfigPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/app-stats" element={
        <AdminRoute>
          <AppStatsPage />
        </AdminRoute>
      } />
      
      <Route path="/apps" element={
        <MasterAdminRoute>
          <AppsPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/app-settings" element={
        <MasterAdminRoute>
          <AppSettingsPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/events" element={
        <AdminRoute>
          <EventsPage />
        </AdminRoute>
      } />
      
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/chat-permissions" element={
        <MasterAdminRoute>
          <ChatPermissionsPage />
        </MasterAdminRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/fleet" element={
        <ProtectedRoute>
          <FleetPage />
        </ProtectedRoute>
      } />
      
      <Route path="/training" element={
        <AdminRoute>
          <TrainingPage />
        </AdminRoute>
      } />
      
      <Route path="/txt-conv" element={
        <AdminRoute>
          <TxtConvPage />
        </AdminRoute>
      } />
      
      <Route path="/" element={<Navigate to="/hub" replace />} />
      <Route path="*" element={<Navigate to="/hub" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors toastOptions={{ style: { marginTop: 'env(safe-area-inset-top, 0px)' } }} />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
